import { parse } from 'json-pointer'

export function renameKeys<T extends Record<string, any>>(
  items: T[],
  mapping: Record<string, string>,
) {
  const colWidths: Record<string, { wch: number; format?: string }> = {}
  const columns = Object.entries(mapping).map(([key, columnLabel]) => ({
    path: key.startsWith('/') ? parse(key) : [key],
    columnLabel,
  }))

  const data = items.map((item: T) =>
    columns.reduce<Record<string, string | number>>(
      (acc, { path, columnLabel }) => {
        const value = path.reduce<unknown>(
          (value, key) =>
            value !== null && typeof value === 'object'
              ? (value as Record<string, unknown>)[key]
              : undefined,
          item,
        )

        if (value == null || value === '-') {
          colWidths[columnLabel] = {
            format: 'text',
            wch: columnLabel.length,
          }
          acc[columnLabel] = ''
          return acc
        }

        /* Array.toString joins with a bare comma, which spreadsheets read as
           a thousands separator and collapse into one huge number. */
        const itemValue = Array.isArray(value) ? value.join(', ') : `${value}`

        let format = colWidths[columnLabel]?.format

        if (!format) {
          if (itemValue.includes('$')) {
            format = '"$"#,##0.00_);\\("$"#,##0.00\\)'
          } else if (/^[-+]?\d+(,\d{3})*(\.\d+)?$/.test(itemValue)) {
            format = '#,##0'
          } else {
            format = 'text'
          }
        }

        const PADDING = 2
        const MAX_COL_WIDTH = 160
        colWidths[columnLabel] = {
          format,
          wch: Math.max(
            Math.min(Math.ceil(itemValue.length + PADDING), MAX_COL_WIDTH),
            Math.ceil(columnLabel.length + PADDING),
            colWidths[columnLabel]?.wch || 0,
          ),
        }

        acc[columnLabel] = itemValue
        return acc
      },
      {},
    ),
  )

  return {
    items: data,
    colWidths,
  }
}
